import React from 'react';
import { Trophy, Zap, Target, CheckCircle, XCircle } from 'lucide-react';
import { StreakDisplay } from '../vocabulary/StreakDisplay';

interface FeatureSummaryProps {
  title: string;
  message: string;
  isPerfect?: boolean;
  score?: number;
  accuracy?: number;
  correctCount?: number;
  incorrectCount?: number;
  currentStreak: number;
  previousStreak: number;
  weekHistory: boolean[];
  onContinue: () => void;
  continueLabel?: string;
  onSecondaryAction?: () => void;
  secondaryLabel?: string;
  colorScheme?: 'purple' | 'orange' | 'pink';
  inline?: boolean;
}

export const FeatureSummary: React.FC<FeatureSummaryProps> = ({
  title,
  message,
  isPerfect = false,
  score,
  accuracy,
  correctCount,
  incorrectCount,
  currentStreak,
  previousStreak,
  weekHistory,
  onContinue,
  continueLabel = 'Continue',
  onSecondaryAction,
  secondaryLabel,
  colorScheme = 'purple',
  inline = false
}) => {
  const colorClasses = {
    purple: {
      gradient: 'from-purple-50 to-indigo-50',
      border: 'border-purple-200',
      text: 'text-purple-600',
      bg: 'bg-purple-600',
      hover: 'hover:bg-purple-700',
      iconBg: 'from-purple-400 to-purple-600'
    },
    orange: {
      gradient: 'from-orange-50 to-red-50',
      border: 'border-orange-200',
      text: 'text-orange-600',
      bg: 'bg-orange-600',
      hover: 'hover:bg-orange-700',
      iconBg: 'from-orange-400 to-orange-600'
    },
    pink: {
      gradient: 'from-pink-50 to-rose-50',
      border: 'border-pink-200',
      text: 'text-pink-600',
      bg: 'bg-pink-600',
      hover: 'hover:bg-pink-700',
      iconBg: 'from-pink-400 to-pink-600'
    }
  };

  const colors = colorClasses[colorScheme];

  return (
    <div className={inline ? "w-full pb-24 md:pb-0" : "min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6 pb-24"}>
      <div className={`bg-white rounded-2xl shadow-xl ${inline ? 'w-full' : 'max-w-md w-full'} overflow-hidden`}>
        {/* Celebration Header */}
        <div className={`relative bg-gradient-to-br ${colors.gradient} p-8 text-center`}>
          {/* Decorative elements */}
          <div className="absolute top-4 left-4 w-16 h-16 bg-purple-200 rounded-full opacity-30 blur-xl"></div>
          <div className="absolute top-4 right-4 w-16 h-16 bg-blue-200 rounded-full opacity-30 blur-xl"></div>
          
          {/* Characters/Illustration area */}
          <div className="relative z-10 mb-4">
            <div className="flex justify-center items-center gap-4 mb-6">
              <div className={`w-20 h-20 bg-gradient-to-br ${colors.iconBg} rounded-full flex items-center justify-center shadow-lg`}>
                <Trophy className="w-10 h-10 text-white" />
              </div>
              <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center shadow-lg">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
            </div>
          </div>

          {/* Main Message */}
          <h1 className={`text-3xl md:text-4xl font-bold ${colors.text} mb-2 relative z-10`}>
            {title}
          </h1>
          <p className="text-gray-600 text-lg relative z-10">
            {message}
          </p>
        </div>

        {/* Stats Boxes */}
        {(score !== undefined || accuracy !== undefined) && (
          <div className="p-6 grid grid-cols-2 gap-4">
            {/* Score/XP */}
            {score !== undefined && (
              <div className={`bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl p-4 border-2 border-yellow-200`}>
                <div className="text-yellow-700 text-xs font-bold uppercase mb-2">Score</div>
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-600" />
                  <span className="text-2xl font-bold text-yellow-700">{score}</span>
                </div>
              </div>
            )}

            {/* Accuracy */}
            {accuracy !== undefined && (
              <div className={`bg-gradient-to-br rounded-xl p-4 border-2 ${
                isPerfect 
                  ? 'from-green-50 to-emerald-50 border-green-200' 
                  : accuracy >= 80
                  ? 'from-blue-50 to-indigo-50 border-blue-200'
                  : 'from-orange-50 to-red-50 border-orange-200'
              }`}>
                <div className={`text-xs font-bold uppercase mb-2 ${
                  isPerfect 
                    ? 'text-green-700' 
                    : accuracy >= 80
                    ? 'text-blue-700'
                    : 'text-orange-700'
                }`}>
                  {isPerfect ? 'Perfect' : accuracy >= 80 ? 'Great' : 'Good Try'}
                </div>
                <div className="flex items-center gap-2">
                  <Target className={`w-5 h-5 ${
                    isPerfect 
                      ? 'text-green-600' 
                      : accuracy >= 80
                      ? 'text-blue-600'
                      : 'text-orange-600'
                  }`} />
                  <span className={`text-2xl font-bold ${
                    isPerfect 
                      ? 'text-green-700' 
                      : accuracy >= 80
                      ? 'text-blue-700'
                      : 'text-orange-700'
                  }`}>
                    {accuracy}%
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Breakdown */}
        {(correctCount !== undefined || incorrectCount !== undefined) && (
          <div className="px-6 pb-4">
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              {correctCount !== undefined && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-gray-700 font-medium">Correct</span>
                  </div>
                  <span className="text-gray-800 font-bold">{correctCount}</span>
                </div>
              )}
              {incorrectCount !== undefined && incorrectCount > 0 && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-red-600" />
                    <span className="text-gray-700 font-medium">Incorrect</span>
                  </div>
                  <span className="text-gray-800 font-bold">{incorrectCount}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Streak Display */}
        <div className="px-6 pb-4">
          <StreakDisplay
            currentStreak={currentStreak}
            previousStreak={previousStreak}
            weekHistory={weekHistory}
            colorScheme={colorScheme}
          />
        </div>

        {/* Action Buttons */}
        <div className="p-6 pt-4">
          {onSecondaryAction ? (
            <div className="space-y-3">
              <button
                onClick={onContinue}
                className={`w-full px-6 py-3 ${colors.bg} text-white rounded-xl font-semibold ${colors.hover} transition-colors`}
              >
                {continueLabel}
              </button>
              <button
                onClick={onSecondaryAction}
                className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
              >
                {secondaryLabel}
              </button>
            </div>
          ) : (
            <button
              onClick={onContinue}
              className={`w-full px-6 py-3 ${colors.bg} text-white rounded-xl font-semibold ${colors.hover} transition-colors`}
            >
              {continueLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
